import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Receipt,
  History,
  Banknote,
  Building2,
  Copy,
  Check,
  ArrowLeft,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import {
  CourierWalletSummary,
  CourierEarningItem,
  CourierWithdrawalItem,
} from '../../types';
import {
  MIN_COURIER_WITHDRAWAL_AMOUNT,
  fetchCourierWalletDashboard,
  requestCourierWithdrawal,
  cancelCourierWithdrawal,
  resolveOutletDisplayName,
} from '../../utils/courierWallet';

interface CourierWalletViewProps {
  courierUsername: string;
  courierDisplayName: string;
  outletId: string;
  onBackToOrders?: () => void;
  onWalletUpdated?: (wallet: CourierWalletSummary) => void;
}

export const CourierWalletView: React.FC<CourierWalletViewProps> = ({
  courierUsername,
  courierDisplayName,
  outletId,
  onBackToOrders,
  onWalletUpdated,
}) => {
  const [wallet, setWallet] = useState<CourierWalletSummary>({
    courierUsername,
    courierName: courierDisplayName,
    outletId,
    totalEarned: 0,
    availableBalance: 0,
    processingBalance: 0,
    totalWithdrawn: 0,
  });
  const [earnings, setEarnings] = useState<CourierEarningItem[]>([]);
  const [withdrawals, setWithdrawals] = useState<CourierWithdrawalItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Sub-tabs inside Courier Wallet: 'EARNINGS' | 'WITHDRAWALS'
  const [historyTab, setHistoryTab] = useState<'EARNINGS' | 'WITHDRAWALS'>('WITHDRAWALS');

  // Withdraw Modal State
  const [showWithdrawModal, setShowWithdrawModal] = useState<boolean>(false);
  const [withdrawAmountInput, setWithdrawAmountInput] = useState<string>('');
  const [targetOutletId, setTargetOutletId] = useState<string>('ANY');
  const [withdrawNotes, setWithdrawNotes] = useState<string>('');
  const [submittingWithdraw, setSubmittingWithdraw] = useState<boolean>(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  // Digital Proof Screen State (BUKTI PENCAIRAN SALDO - NO QR/BARCODE)
  const [selectedProofWithdrawal, setSelectedProofWithdrawal] = useState<CourierWithdrawalItem | null>(null);
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadWalletData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        const data = await fetchCourierWalletDashboard();
        setWallet(data.wallet);
        setEarnings(data.earnings);
        setWithdrawals(data.withdrawals);
        setErrorMsg(null);
        if (onWalletUpdated) {
          onWalletUpdated(data.wallet);
        }
        if (selectedProofWithdrawal) {
          const updatedProof = data.withdrawals.find(
            (w) =>
              w.id === selectedProofWithdrawal.id ||
              w.transactionCode === selectedProofWithdrawal.transactionCode
          );
          if (updatedProof) {
            setSelectedProofWithdrawal(updatedProof);
          }
        }
      } catch (err: any) {
        if (!silent) {
          setErrorMsg(err?.message || 'Gagal memuat data Dompet Kurir.');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [onWalletUpdated, selectedProofWithdrawal]
  );

  useEffect(() => {
    loadWalletData(false);
    const interval = setInterval(() => loadWalletData(true), 12000);
    return () => clearInterval(interval);
  }, [loadWalletData]);

  const formatRupiah = (amount: number) => `Rp${Number(amount || 0).toLocaleString('id-ID')}`;

  const formatDateTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const handleOpenWithdrawModal = () => {
    setWithdrawError(null);
    const defaultAmount =
      wallet.availableBalance >= MIN_COURIER_WITHDRAWAL_AMOUNT
        ? String(wallet.availableBalance)
        : String(MIN_COURIER_WITHDRAWAL_AMOUNT);
    setWithdrawAmountInput(defaultAmount);
    setTargetOutletId('ANY');
    setWithdrawNotes('');
    setShowWithdrawModal(true);
  };

  const handleSubmitWithdrawal = async (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawError(null);

    const parsedAmount = Math.floor(Number(String(withdrawAmountInput).replace(/[^0-9]/g, '') || 0));
    if (!Number.isFinite(parsedAmount) || parsedAmount < MIN_COURIER_WITHDRAWAL_AMOUNT) {
      setWithdrawError('Minimal pencairan saldo adalah Rp50.000.');
      return;
    }

    if (parsedAmount > wallet.availableBalance) {
      setWithdrawError(
        `Saldo tersedia (${formatRupiah(wallet.availableBalance)}) tidak mencukupi untuk nominal ${formatRupiah(parsedAmount)}.`
      );
      return;
    }

    setSubmittingWithdraw(true);
    try {
      const res = await requestCourierWithdrawal({
        amount: parsedAmount,
        targetOutletId,
        notes: withdrawNotes.trim(),
      });

      setWallet(res.wallet);
      setWithdrawals((prev) => [res.withdrawal, ...prev.filter((w) => w.id !== res.withdrawal.id)]);
      if (onWalletUpdated) onWalletUpdated(res.wallet);

      setShowWithdrawModal(false);
      // Immediately open the Digital Withdrawal Proof screen
      setSelectedProofWithdrawal(res.withdrawal);
      setSuccessMsg(`Pengajuan pencairan ${formatRupiah(res.withdrawal.amount)} berhasil dibuat.`);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err: any) {
      setWithdrawError(err?.message || 'Gagal mengajukan pencairan saldo.');
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawal: CourierWithdrawalItem) => {
    if (withdrawal.status !== 'PENDING' || cancellingId) return;
    setCancellingId(withdrawal.id);
    setErrorMsg(null);
    try {
      const res = await cancelCourierWithdrawal(withdrawal.id, 'Dibatalkan oleh kurir melalui aplikasi');
      setWallet(res.wallet);
      setWithdrawals((prev) =>
        prev.map((w) => (w.id === withdrawal.id ? res.withdrawal : w))
      );
      if (selectedProofWithdrawal?.id === withdrawal.id) {
        setSelectedProofWithdrawal(res.withdrawal);
      }
      if (onWalletUpdated) onWalletUpdated(res.wallet);
      setSuccessMsg(
        `Pencairan #${withdrawal.transactionCode} dibatalkan dan saldo ${formatRupiah(withdrawal.amount)} telah dikembalikan ke Saldo Tersedia.`
      );
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Gagal membatalkan pengajuan pencairan.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleCopyCode = (code: string) => {
    try {
      navigator.clipboard.writeText(code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {}
  };

  // =========================================================================
  // RENDER: DIGITAL WITHDRAWAL PROOF SCREEN (BUKTI PENCAIRAN SALDO)
  // Strictly NO QR Code and NO Barcode per specification
  // =========================================================================
  if (selectedProofWithdrawal) {
    const isPending = selectedProofWithdrawal.status === 'PENDING';
    const isPaid = selectedProofWithdrawal.status === 'PAID';
    const isCancelled = selectedProofWithdrawal.status === 'CANCELLED';

    const statusBadgeText = isPending
      ? 'MENUNGGU PEMBAYARAN'
      : isPaid
      ? 'SUDAH DIBAYAR TUNAI'
      : 'DIBATALKAN';

    return (
      <div className="max-w-lg mx-auto py-2 px-1">
        {/* Top Action Bar */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setSelectedProofWithdrawal(null)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-bold shadow-xs transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Dompet
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedProofWithdrawal(null);
              setHistoryTab('WITHDRAWALS');
            }}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-blue-50 border border-blue-200 text-[#0A5C99] hover:bg-blue-100 text-xs font-bold transition-all"
          >
            <History className="w-4 h-4" />
            Riwayat Pencairan
          </button>
        </div>

        {/* Screenshot-Ready Digital Receipt Card */}
        <div className="bg-white rounded-3xl border-2 border-[#0A5C99]/20 shadow-xl overflow-hidden">
          {/* Header Banner with LetonKu Logo */}
          <div className="bg-gradient-to-br from-[#063E6B] via-[#0A5C99] to-[#0E74BC] px-6 py-6 text-center text-white relative">
            <div className="flex justify-center mb-3">
              <div className="bg-white px-4 py-2 rounded-2xl shadow-md inline-flex items-center justify-center">
                <img
                  src="/assets/letonku-header-logo.png"
                  alt="LetonKu"
                  className="h-7 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-100">
              Dokumen Resmi Kurir LetonKu
            </p>
            <h2 className="text-lg sm:text-xl font-black tracking-wide uppercase mt-1">
              BUKTI PENCAIRAN SALDO
            </h2>
          </div>

          {/* Nominal & Status Section */}
          <div className="px-6 pt-6 pb-5 text-center border-b border-dashed border-slate-200 bg-slate-50/60">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Nominal Pencairan Tunai
            </p>
            <p className="text-3xl sm:text-4xl font-black text-slate-900 mt-1 tracking-tight">
              {formatRupiah(selectedProofWithdrawal.amount)}
            </p>

            <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider border shadow-2xs
              ${
                isPending
                  ? 'bg-amber-50 text-amber-800 border-amber-300'
                  : isPaid
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-rose-50 text-rose-800 border-rose-300'
              }"
              style={{
                backgroundColor: isPending ? '#FFFBEB' : isPaid ? '#ECFDF5' : '#FFF1F2',
                color: isPending ? '#92400E' : isPaid ? '#065F46' : '#9F1239',
                borderColor: isPending ? '#FCD34D' : isPaid ? '#6EE7B7' : '#FDA4AF',
              }}
            >
              {isPending && <Clock className="w-3.5 h-3.5 text-amber-600" />}
              {isPaid && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
              {isCancelled && <XCircle className="w-3.5 h-3.5 text-rose-600" />}
              <span>Status: {statusBadgeText}</span>
            </div>
          </div>

          {/* Unique Transaction ID Highlight Box (No QR / No Barcode) */}
          <div className="px-6 py-5 bg-white">
            <div className="bg-blue-50/80 border-2 border-[#0A5C99]/25 rounded-2xl p-4 text-center">
              <p className="text-[11px] font-extrabold uppercase tracking-widest text-[#0A5C99]">
                ID Transaksi Pencairan Unik
              </p>
              <div className="mt-1.5 flex items-center justify-center gap-2">
                <span className="font-mono text-xl sm:text-2xl font-black tracking-wider text-slate-900 select-all">
                  {selectedProofWithdrawal.transactionCode}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopyCode(selectedProofWithdrawal.transactionCode)}
                  className="p-2 rounded-xl bg-white border border-blue-200 text-[#0A5C99] hover:bg-blue-100 transition-colors"
                  title="Salin ID Transaksi"
                >
                  {copiedCode ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-600 mt-1">
                Kasir akan mencari ID transaksi ini di menu <strong>Pencairan Kurir</strong>
              </p>
            </div>

            {/* Detailed Metadata Rows */}
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Nama Kurir</span>
                <span className="font-bold text-slate-900 text-right">
                  {selectedProofWithdrawal.courierName}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Username Kurir</span>
                <span className="font-mono font-semibold text-slate-700">
                  @{selectedProofWithdrawal.courierUsername}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Tanggal & Waktu Pengajuan</span>
                <span className="font-semibold text-slate-800 text-right">
                  {formatDateTime(selectedProofWithdrawal.requestedAt)}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Metode Pencairan</span>
                <span className="font-bold text-[#0A5C99]">TUNAI MELALUI KASIR</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Lokasi Pencairan</span>
                <span className="font-semibold text-slate-800 text-right">
                  {selectedProofWithdrawal.paidAtOutletName
                    ? selectedProofWithdrawal.paidAtOutletName
                    : resolveOutletDisplayName(selectedProofWithdrawal.targetOutletId || 'ANY')}
                </span>
              </div>

              {selectedProofWithdrawal.paidAt && (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Dibayar Oleh Kasir</span>
                    <span className="font-bold text-emerald-700 text-right">
                      {selectedProofWithdrawal.paidByCashierName || selectedProofWithdrawal.paidByCashierUsername}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 font-medium">Waktu Pembayaran</span>
                    <span className="font-semibold text-emerald-700 text-right">
                      {formatDateTime(selectedProofWithdrawal.paidAt)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Bold Callout Banner */}
            <div className="mt-5 rounded-2xl bg-[#0A5C99] text-white px-4 py-3.5 text-center shadow-sm">
              <p className="text-xs sm:text-sm font-black tracking-wider uppercase">
                TUNJUKKAN BUKTI INI KEPADA KASIR
              </p>
              <p className="text-[11px] text-blue-100 mt-0.5">
                Dapat dicairkan di kasir outlet LetonKu mana pun
              </p>
            </div>

            {/* Important Verification Disclaimer */}
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 text-left">
              <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-900 leading-relaxed">
                <strong>Catatan Penting:</strong> Tangkapan layar (screenshot) bukti ini hanya menjadi{' '}
                <strong>referensi ID transaksi</strong> bagi kasir. Pencairan baru dinyatakan sah dan selesai setelah kasir menekan tombol{' '}
                <strong>KONFIRMASI PEMBAYARAN</strong> di sistem Kasir LetonKu dan status berubah menjadi{' '}
                <strong>SUDAH DIBAYAR</strong>.
              </p>
            </div>

            {/* Bottom Actions */}
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              {onBackToOrders && (
                <button
                  type="button"
                  onClick={onBackToOrders}
                  className="flex-1 py-3 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-extrabold uppercase tracking-wider transition-all"
                >
                  Kembali ke Dashboard
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelectedProofWithdrawal(null);
                  setHistoryTab('WITHDRAWALS');
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-[#0A5C99] hover:bg-[#084879] text-white text-xs font-extrabold uppercase tracking-wider transition-all"
              >
                Lihat Riwayat Pencairan
              </button>
            </div>

            {isPending && (
              <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                <button
                  type="button"
                  disabled={cancellingId === selectedProofWithdrawal.id}
                  onClick={() => handleCancelWithdrawal(selectedProofWithdrawal)}
                  className="text-xs font-bold text-rose-600 hover:text-rose-700 underline disabled:opacity-50"
                >
                  {cancellingId === selectedProofWithdrawal.id
                    ? 'Membatalkan pengajuan...'
                    : 'Batalkan Pengajuan & Kembalikan Saldo'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER: MAIN COURIER WALLET DASHBOARD
  // =========================================================================
  const canWithdraw = wallet.availableBalance >= MIN_COURIER_WITHDRAWAL_AMOUNT;

  return (
    <div className="space-y-5">
      {/* Status Notifications */}
      {errorMsg && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3 text-rose-900">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs sm:text-sm font-medium">{errorMsg}</div>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            className="text-xs font-bold text-rose-700 hover:underline"
          >
            Tutup
          </button>
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3 text-emerald-900">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs sm:text-sm font-medium">{successMsg}</div>
        </div>
      )}

      {/* Primary Hero Wallet Card */}
      <div className="bg-gradient-to-br from-[#063E6B] via-[#0A5C99] to-[#0E74BC] rounded-3xl p-5 sm:p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-blue-100 text-[11px] font-bold uppercase tracking-wider">
              <Wallet className="w-3.5 h-3.5" />
              <span>Dompet Penghasilan Kurir (100% Ongkir)</span>
            </div>
            <p className="text-xs text-blue-100 mt-3 font-medium">Saldo Tersedia Dapat Dicairkan</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-0.5">
              {formatRupiah(wallet.availableBalance)}
            </h2>
            <p className="text-[11px] text-blue-100/90 mt-1">
              Minimal pencairan <strong>Rp50.000</strong> • Tunai melalui Kasir di semua outlet LetonKu
            </p>
          </div>

          <div className="flex flex-col sm:items-end gap-2">
            <button
              type="button"
              onClick={handleOpenWithdrawModal}
              disabled={!canWithdraw}
              className={`inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-extrabold text-xs sm:text-sm uppercase tracking-wider shadow-md transition-all ${
                canWithdraw
                  ? 'bg-white text-[#0A5C99] hover:bg-blue-50 cursor-pointer'
                  : 'bg-white/20 text-white/60 cursor-not-allowed'
              }`}
            >
              <Banknote className="w-4 h-4" />
              <span>CAIRKAN SALDO</span>
            </button>
            {!canWithdraw && (
              <span className="text-[11px] text-amber-200 font-semibold">
                Butuh {formatRupiah(Math.max(0, MIN_COURIER_WITHDRAWAL_AMOUNT - wallet.availableBalance))} lagi untuk pencairan
              </span>
            )}
          </div>
        </div>

        {/* 4 Summary Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/15">
          <div className="bg-white/10 rounded-2xl p-3.5 backdrop-blur-xs">
            <div className="flex items-center gap-1.5 text-blue-100 text-[11px] font-semibold">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Total Penghasilan</span>
            </div>
            <p className="text-base sm:text-lg font-extrabold mt-1">
              {formatRupiah(wallet.totalEarned)}
            </p>
          </div>

          <div className="bg-white/10 rounded-2xl p-3.5 backdrop-blur-xs">
            <div className="flex items-center gap-1.5 text-emerald-200 text-[11px] font-semibold">
              <Wallet className="w-3.5 h-3.5" />
              <span>Saldo Tersedia</span>
            </div>
            <p className="text-base sm:text-lg font-extrabold mt-1 text-emerald-100">
              {formatRupiah(wallet.availableBalance)}
            </p>
          </div>

          <div className="bg-white/10 rounded-2xl p-3.5 backdrop-blur-xs">
            <div className="flex items-center gap-1.5 text-amber-200 text-[11px] font-semibold">
              <Clock className="w-3.5 h-3.5" />
              <span>Sedang Diproses</span>
            </div>
            <p className="text-base sm:text-lg font-extrabold mt-1 text-amber-100">
              {formatRupiah(wallet.processingBalance)}
            </p>
          </div>

          <div className="bg-white/10 rounded-2xl p-3.5 backdrop-blur-xs">
            <div className="flex items-center gap-1.5 text-sky-200 text-[11px] font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Sudah Dicairkan</span>
            </div>
            <p className="text-base sm:text-lg font-extrabold mt-1">
              {formatRupiah(wallet.totalWithdrawn)}
            </p>
          </div>
        </div>
      </div>

      {/* Active Pending Withdrawals Quick Banner */}
      {withdrawals.some((w) => w.status === 'PENDING') && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-extrabold uppercase tracking-wider text-amber-900">
                  Pencairan Menunggu Pembayaran Kasir
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Tunjukkan bukti pencairan digital kepada kasir outlet LetonKu untuk menerima uang tunai.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const pending = withdrawals.find((w) => w.status === 'PENDING');
                if (pending) setSelectedProofWithdrawal(pending);
              }}
              className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-extrabold uppercase tracking-wider transition-all shrink-0"
            >
              Buka Bukti Pencairan
            </button>
          </div>
        </div>
      )}

      {/* History Tabs: Riwayat Pencairan & Riwayat Penghasilan */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 pt-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setHistoryTab('WITHDRAWALS')}
              className={`px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider border-b-2 transition-all ${
                historyTab === 'WITHDRAWALS'
                  ? 'border-[#0A5C99] text-[#0A5C99]'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Riwayat Pencairan ({withdrawals.length})
            </button>
            <button
              type="button"
              onClick={() => setHistoryTab('EARNINGS')}
              className={`px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider border-b-2 transition-all ${
                historyTab === 'EARNINGS'
                  ? 'border-[#0A5C99] text-[#0A5C99]'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Riwayat Penghasilan ({earnings.length})
            </button>
          </div>

          <button
            type="button"
            onClick={() => loadWalletData(true)}
            disabled={refreshing}
            className="p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            title="Segarkan Data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-[#0A5C99]' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Memuat data dompet kurir...</div>
        ) : historyTab === 'WITHDRAWALS' ? (
          withdrawals.length === 0 ? (
            <div className="p-8 text-center">
              <Banknote className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-700">Belum Ada Riwayat Pencairan</p>
              <p className="text-xs text-slate-500 mt-1">
                Kumpulkan saldo ongkir minimal Rp50.000 lalu klik tombol <strong>CAIRKAN SALDO</strong>.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {withdrawals.map((wd) => {
                const isPending = wd.status === 'PENDING';
                const isPaid = wd.status === 'PAID';
                return (
                  <div
                    key={wd.id}
                    className="p-4 hover:bg-slate-50/70 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-black text-[#0A5C99] bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200">
                          {wd.transactionCode}
                        </span>
                        <span
                          className={`text-[11px] font-extrabold px-2.5 py-0.5 rounded-full uppercase ${
                            isPending
                              ? 'bg-amber-100 text-amber-800'
                              : isPaid
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {isPending
                            ? 'MENUNGGU PEMBAYARAN'
                            : isPaid
                            ? 'DIBAYAR TUNAI'
                            : 'DIBATALKAN'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Diajukan: {formatDateTime(wd.requestedAt)} • Metode: {wd.paymentMethod}
                      </p>
                      {isPaid && (
                        <p className="text-xs text-emerald-700 font-semibold">
                          Dibayar oleh {wd.paidByCashierName || wd.paidByCashierUsername} di{' '}
                          {wd.paidAtOutletName || wd.paidAtOutletId} ({wd.paidAt ? formatDateTime(wd.paidAt) : ''})
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <div className="text-left sm:text-right">
                        <p className="text-base font-black text-slate-900">
                          {formatRupiah(wd.amount)}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedProofWithdrawal(wd)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-[#0A5C99] text-xs font-extrabold transition-colors"
                        >
                          <Receipt className="w-3.5 h-3.5" />
                          <span>Bukti Pencairan</span>
                        </button>

                        {isPending && (
                          <button
                            type="button"
                            disabled={cancellingId === wd.id}
                            onClick={() => handleCancelWithdrawal(wd)}
                            className="px-3 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold transition-colors disabled:opacity-50"
                          >
                            Batal
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : earnings.length === 0 ? (
          <div className="p-8 text-center">
            <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-700">Belum Ada Riwayat Penghasilan</p>
            <p className="text-xs text-slate-500 mt-1">
              Penghasilan 100% ongkir otomatis masuk setelah pesanan Delivery selesai diantar (DELIVERED) dan pembayaran pelanggan terverifikasi (PAID).
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {earnings.map((item) => {
              const isCredited = item.status === 'CREDITED';
              const isReversed = item.status === 'REVERSED';
              return (
                <div
                  key={item.id}
                  className="p-4 hover:bg-slate-50/70 transition-colors flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black text-slate-900">
                        Pesanan #{item.orderNumber.replace(/^#/, '')}
                      </span>
                      <span
                        className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase ${
                          isCredited
                            ? 'bg-emerald-100 text-emerald-800'
                            : isReversed
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {isCredited ? 'MASUK SALDO' : isReversed ? 'DIREVERSAL' : 'PERLU REKONSILIASI'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDateTime(item.earnedAt)} • 100% Ongkir Pelanggan
                    </p>
                    {item.reversalReason && (
                      <p className="text-[11px] text-rose-600 mt-0.5">{item.reversalReason}</p>
                    )}
                  </div>

                  <div className="text-right">
                    <p
                      className={`text-sm sm:text-base font-black ${
                        isCredited
                          ? 'text-emerald-700'
                          : isReversed
                          ? 'text-rose-600 line-through'
                          : 'text-amber-700'
                      }`}
                    >
                      {isCredited ? '+' : ''}
                      {formatRupiah(item.amount)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal: Ajukan Pencairan Saldo */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-black text-slate-900 uppercase">
                  Cairkan Saldo Kurir
                </h3>
                <p className="text-xs text-slate-500">
                  Metode pencairan: Tunai melalui Kasir Outlet LetonKu
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowWithdrawModal(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitWithdrawal} className="mt-4 space-y-4">
              <div className="bg-blue-50/80 border border-blue-200 rounded-2xl p-3.5 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-600">Saldo Tersedia Saat Ini</span>
                <span className="text-base font-black text-[#0A5C99]">
                  {formatRupiah(wallet.availableBalance)}
                </span>
              </div>

              {withdrawError && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-xs font-semibold flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{withdrawError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Nominal Pencairan (Minimal Rp50.000)
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">
                    Rp
                  </span>
                  <input
                    type="number"
                    min={MIN_COURIER_WITHDRAWAL_AMOUNT}
                    max={wallet.availableBalance}
                    step={1000}
                    value={withdrawAmountInput}
                    onChange={(e) => setWithdrawAmountInput(e.target.value)}
                    className="w-full pl-10 pr-24 py-3 rounded-xl border border-slate-300 font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A5C99]"
                    placeholder="50000"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setWithdrawAmountInput(String(wallet.availableBalance))}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg bg-blue-50 text-[#0A5C99] text-[11px] font-extrabold hover:bg-blue-100"
                  >
                    CAIRKAN SEMUA
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Rencana Outlet Pencairan Tunai
                </label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <select
                    value={targetOutletId}
                    onChange={(e) => setTargetOutletId(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A5C99]"
                  >
                    <option value="ANY">Semua Outlet LetonKu (Bebas Pilih Kasir)</option>
                    <option value="sudirman">Chapter 5 - Leton Sudirman</option>
                    <option value="kelakap_7">Chapter 6 - Leton Kelakap 7</option>
                    <option value="letgo-mpp">Let&apos;GO - MPP & Mobile</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Catatan Tambahan (Opsional)
                </label>
                <input
                  type="text"
                  value={withdrawNotes}
                  onChange={(e) => setWithdrawNotes(e.target.value)}
                  placeholder="Contoh: Ambil tunai sore hari"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A5C99]"
                />
              </div>

              <div className="bg-slate-50 rounded-xl p-3 text-[11px] text-slate-600 leading-relaxed">
                Begitu Anda menekan <strong>Ajukan Pencairan</strong>, saldo sebesar nominal di atas akan langsung ditahan ke <strong>Saldo Sedang Diproses</strong> dan sistem akan menampilkan <strong>Bukti Pencairan Digital</strong> untuk ditunjukkan kepada kasir.
              </div>

              <div className="flex items-center gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(false)}
                  className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submittingWithdraw}
                  className="flex-1 py-3 rounded-xl bg-[#0A5C99] hover:bg-[#084879] text-white text-xs font-extrabold uppercase tracking-wider shadow-md disabled:opacity-50"
                >
                  {submittingWithdraw ? 'Memproses...' : 'Ajukan Pencairan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
